import { useState, useMemo } from 'react';
import { FiChevronUp } from 'react-icons/fi';
import { useTranslation } from 'next-i18next';
import Accordion from '../Accordion/Accordion';
import Counter, { getTotalChildCount, getTotalInfantCount } from './Counter';
import type { RoomAndGuestCount } from '../../booking/meal-plan/types';
import useIsAboveBreakpoint from '../../../core/hooks/useIsAboveBreakpoint';

type Props = {
  count: RoomAndGuestCount;
  setCount: (operation: 'add' | 'remove', type: keyof RoomAndGuestCount) => void;
  roundedSearchBar?: boolean;
};

const ChildCounterAccordion = ({ count, setCount, roundedSearchBar }: Props) => {
  const [isChildAccordionOpen, setIsChildAccordionOpen] = useState(false);
  const { t } = useTranslation();
  const isAboveMD = useIsAboveBreakpoint('MD');

  const content = useMemo(
    () => (
      <div className="flex flex-col gap-y-2 pt-4">
        <div className="flex flex-col gap-y-2">
          <div className="flex justify-center bg-gray-50 py-1">
            <p className="text-mini font-bold">
              {t('component:child_counter_accordion.elementary_school_child')}
            </p>
          </div>
          <div className="flex items-center justify-between">
            {/* TODO: DC-1116 put this back once Almex resolves the issue */}
            {/* <p className="text-small">{t('component:child_counter_accordion.senior_elementary_school_child')}</p> */}
            <p className="text-small">
              {t('component:child_counter_accordion.elementary_school_child')}
            </p>
            <Counter
              count={count}
              guestType="seniorElementarySchoolChildCount"
              setCount={setCount}
            />
          </div>
          {/* TODO: DC-1116 put this back once Almex resolves the issue */}
          {/* <div className="flex justify-between items-center">
            <p className="text-small">{t('component:child_counter_accordion.junior_elementary_school_child')}</p>
            <Counter
              count={count}
              guestType="juniorElementarySchoolChildCount"
              setCount={setCount}
            />
          </div> */}
        </div>
        <div className="flex flex-col gap-y-2">
          <div className="flex justify-center bg-gray-50 py-1">
            <p className="text-mini font-bold">{t('component:child_counter_accordion.infant')}</p>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-small">{t('component:child_counter_accordion.with_meal_futon')}</p>
            <Counter count={count} guestType="infantWithMealAndFutonCount" setCount={setCount} />
          </div>
          {/* TODO: DC-1116 put this back once Almex resolves the issue */}
          {/* <div className="flex justify-between items-center">
            <p className="text-small">{t('component:child_counter_accordion.with_meal')}</p>
            <Counter count={count} guestType="infantWithMealCount" setCount={setCount} />
          </div> */}
          <div className="flex items-center justify-between">
            <p className="text-small">{t('component:child_counter_accordion.with_futon')}</p>
            <Counter count={count} guestType="infantWithFutonCount" setCount={setCount} />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-small">
              {t('component:child_counter_accordion.without_meal_futon')}
            </p>
            <Counter count={count} guestType="infantWithoutMealAndFutonCount" setCount={setCount} />
          </div>
        </div>
      </div>
    ),
    [count, setCount, t]
  );

  const button = (
    <button
      type="button"
      className="flex w-full items-center justify-between border-b-2 border-dotted border-gray-200 py-4"
      onClick={() => setIsChildAccordionOpen(!isChildAccordionOpen)}
    >
      <p className="text-small font-bold">{t('component:child_counter_accordion.child')}</p>
      <div className="flex gap-6">
        <p>{getTotalChildCount(count)}</p>
        <p
          className={`${
            isChildAccordionOpen ? 'ease duration-700' : 'ease rotate-180 duration-700'
          } inline-block`}
        >
          <FiChevronUp size="20" className="text-orange-700" />
        </p>
      </div>
    </button>
  );

  if (isAboveMD && roundedSearchBar) {
    return (
      <div className="flex flex-col gap-8">
        <div className="flex items-center justify-between">
          <p className="text-regular">{t('component:child_counter_accordion.infant')}</p>
          <div className="w-[123px] text-center">
            <p className="text-xl font-bold">{getTotalInfantCount(count)}</p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-regular">{t('component:child_counter_accordion.with_meal_futon')}</p>
          <Counter
            roundedSearchBar
            count={count}
            guestType="infantWithMealAndFutonCount"
            setCount={setCount}
          />
        </div>
        <div className="flex items-center justify-between">
          <p className="text-regular">{t('component:child_counter_accordion.with_futon')}</p>
          <Counter
            roundedSearchBar
            count={count}
            guestType="infantWithFutonCount"
            setCount={setCount}
          />
        </div>
        <div className="flex items-center justify-between">
          <p className="text-regular">
            {t('component:child_counter_accordion.without_meal_futon')}
          </p>
          <Counter
            roundedSearchBar
            count={count}
            guestType="infantWithoutMealAndFutonCount"
            setCount={setCount}
          />
        </div>
      </div>
    );
  }

  return (
    <Accordion
      content={content}
      button={button}
      open={isChildAccordionOpen}
      borderColor="none"
      direction="bottom"
    />
  );
};

export default ChildCounterAccordion;
